/**
 * Local Discount Calculator
 *
 * Calculates estimated discount amounts for SalesRule action types
 * directly on the client side. Server remains the final authority —
 * this is for instant UX feedback (estimated savings display).
 *
 * Supported actions:
 * - by_percent: item subtotal × (discount_amount / 100)
 * - by_fixed: discount_amount × total qty
 * - cart_fixed: min(discount_amount, subtotal) — distribution differs from server
 *
 * NOT supported (server-only):
 * - buy_x_get_y: Requires promo gift logic + stock availability
 *
 * @module module-quote/helpers/local-discount-calculator
 */

export type SalesRuleActionType = 'by_percent' | 'by_fixed' | 'cart_fixed' | 'buy_x_get_y';

export type DiscountConfidenceLevel = 'exact-local' | 'estimated' | 'server-required';

export interface DiscountEstimationInput {
    /** SalesRule simple_action field */
    simpleAction: SalesRuleActionType;
    /** SalesRule discount_amount field */
    discountAmount: number;
    /** Cart subtotal (sum of selected items row_total) */
    cartSubtotal: number;
    /** Cart items with qty and row total for per-item calculations */
    cartItems: ReadonlyArray<{
        readonly sku: string;
        readonly qty: number;
        readonly rowTotal: number;
    }>;
    /** Maximum qty the discount applies to (SalesRule discount_qty) */
    discountQty?: number;
    /** Step for buy_x_get_y rules (SalesRule discount_step) */
    discountStep?: number;
    /** Maximum discount cap (custom field, null = no cap) */
    maxDiscountAmount?: number;
}

export interface DiscountEstimationResult {
    /** Estimated discount amount in cart currency (VND — always integer) */
    estimatedDiscount: number;
    /** Estimated grand total after discount (subtotal - discount) */
    estimatedGrandTotal: number;
    /**
     * Whether this estimation is exact (true for by_percent/by_fixed on simple carts)
     * or approximate (cart_fixed distribution may differ from server).
     */
    isExact: boolean;
    /**
     * Confidence level:
     * - 'exact-local': Calculation is expected to match server exactly
     * - 'estimated': Approximate, server may differ slightly
     * - 'server-required': Cannot calculate locally, must wait for server
     */
    confidence: DiscountConfidenceLevel;
}

const ZERO_RESULT: DiscountEstimationResult = {
    estimatedDiscount: 0,
    estimatedGrandTotal: 0,
    isExact: false,
    confidence: 'server-required',
};

/**
 * Calculate estimated discount locally based on SalesRule action type.
 *
 * @param input - Rule data + cart context
 * @returns Estimated discount and confidence level
 */
export const calculateLocalDiscount = (input: DiscountEstimationInput): DiscountEstimationResult => {
    const { simpleAction, discountAmount, cartSubtotal, cartItems, maxDiscountAmount } = input;

    if (discountAmount <= 0 || cartSubtotal <= 0) {
        return { ...ZERO_RESULT, estimatedGrandTotal: Math.max(0, cartSubtotal) };
    }

    let estimatedDiscount = 0;
    let isExact = false;
    let confidence: DiscountConfidenceLevel = 'server-required';

    switch (simpleAction) {
        case 'by_percent': {
            // Percentage discount applied to each eligible item's row_total.
            // Magento: foreach item → item.row_total × (discountAmount / 100)
            // For local estimation: subtotal × (discountAmount / 100)
            // This is exact when ALL items are eligible (no product-specific conditions).
            const percentFactor = Math.min(discountAmount, 100) / 100;
            estimatedDiscount = cartSubtotal * percentFactor;
            isExact = true;
            confidence = 'exact-local';
            break;
        }

        case 'by_fixed': {
            // Fixed amount per item: discountAmount × total qty of eligible items.
            // Magento: foreach item → min(discountAmount × item.qty, item.row_total)
            const totalQty = cartItems.reduce((sum, item) => sum + item.qty, 0);
            estimatedDiscount = discountAmount * totalQty;

            // Per-item cap: discount per item cannot exceed item price
            // Recalculate with per-item capping for accuracy
            let cappedDiscount = 0;
            for (const item of cartItems) {
                const itemDiscount = Math.min(discountAmount * item.qty, item.rowTotal);
                cappedDiscount += itemDiscount;
            }
            estimatedDiscount = cartItems.length > 0 ? cappedDiscount : estimatedDiscount;

            isExact = true;
            confidence = 'exact-local';
            break;
        }

        case 'cart_fixed': {
            // Fixed total discount across entire cart.
            // Magento distributes proportionally across items, which may cause
            // rounding differences. For estimation, the total is accurate.
            estimatedDiscount = Math.min(discountAmount, cartSubtotal);
            isExact = false; // Distribution may differ from server
            confidence = 'estimated';
            break;
        }

        case 'buy_x_get_y':
        default: {
            // Too complex for local calculation (requires promo gift logic)
            return {
                estimatedDiscount: 0,
                estimatedGrandTotal: cartSubtotal,
                isExact: false,
                confidence: 'server-required',
            };
        }
    }

    // Apply maximum discount cap if specified by the rule
    if (maxDiscountAmount != null && maxDiscountAmount > 0) {
        estimatedDiscount = Math.min(estimatedDiscount, maxDiscountAmount);
    }

    // Discount cannot exceed subtotal
    estimatedDiscount = Math.min(estimatedDiscount, cartSubtotal);

    // VND has no decimal places — round to integer
    estimatedDiscount = Math.round(estimatedDiscount);

    return {
        estimatedDiscount,
        estimatedGrandTotal: Math.max(0, cartSubtotal - estimatedDiscount),
        isExact,
        confidence,
    };
};

/**
 * Check if a SalesRule action type is supported for local discount calculation.
 */
export const isLocallyCalculable = (simpleAction: string | null | undefined): boolean => {
    return simpleAction === 'by_percent' || simpleAction === 'by_fixed' || simpleAction === 'cart_fixed';
};
